import { useEffect, useContext } from 'react';
import {
  Box, Typography, Paper, TextField, Button, MenuItem, Select,
  FormControl, InputLabel, CircularProgress, FormControlLabel, IconButton,
  Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, Switch, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AuthContext } from '../context/AuthProvider';
import { stringifyAddress } from '../utils/formatter';
import { calculateTotalPay } from '../utils/proposalCalculations';
import WarrantyTicketFormSections from './WarrantyTicketFormSections';
import useWarrantyTicketForm, { STEP_LABELS } from '../hooks/useWarrantyTicketForm';

export default function WarrantyTicketCreateDialog({ open, onClose, propertyId, onCreated }) {
  const { userData } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const ctl = useWarrantyTicketForm({
    userData,
    onSuccess: () => {
      if (onCreated) onCreated();
      onClose();
    },
  });

  const {
    isAdminLike, expertMode, setExpertMode, currentStep, goNextStep, goPrevStep,
    selectedProperty, propertyLoading, proposals, legacyProposals, notAirmaxxInstallation,
    clientInfo, ccProjectUrl, proposalSelectValue, orderType, form, errors, submitting, showReview,
    resetForm, loadPropertyById, handleProposalSelect, handleNotAirmaxxToggle, handleFormChange, handleSubmit,
  } = ctl;

  useEffect(() => {
    if (open && propertyId) {
      resetForm();
      loadPropertyById(propertyId);
    }
  }, [open, propertyId, resetForm, loadPropertyById]);

  const addressSlot = (expertMode || currentStep === 1) && (
    <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>Address & Customer</Typography>
        {ccProjectUrl && (
          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ccProjectUrl} target="_blank" rel="noopener noreferrer">
            Open CompanyCam
          </Button>
        )}
      </Box>
      {selectedProperty && (
        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{stringifyAddress(selectedProperty)}</Typography>
          {clientInfo && (
            <Typography variant="body2" color="text.secondary">
              Customer: {clientInfo.firstName} {clientInfo.lastName}
              {clientInfo.phoneNumber && ` • ${clientInfo.phoneNumber}`}
            </Typography>
          )}
        </Box>
      )}

      {(proposals.length > 0 || legacyProposals.length > 0) && (
        <FormControl fullWidth size="small" error={!!errors.proposal} sx={{ mb: 1 }} disabled={notAirmaxxInstallation}>
          <InputLabel>Select Proposal *</InputLabel>
          <Select value={proposalSelectValue} label="Select Proposal *" onChange={(e) => handleProposalSelect(e.target.value)}>
            {proposals.map((p) => {
              const amount = calculateTotalPay(p);
              return (
                <MenuItem key={p.id} value={p.id}>
                  {p.publicId || p.id?.slice(0, 8)} — {p.status}{p.proposalType && ` (${p.proposalType})`}
                  {amount > 0 && ` — ${amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                </MenuItem>
              );
            })}
            {legacyProposals.length > 0 && proposals.length > 0 && (
              <MenuItem disabled divider sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>— Legacy Proposals —</MenuItem>
            )}
            {legacyProposals.map((lp) => (
              <MenuItem key={`legacy:${lp.proposalNumber}`} value={`legacy:${lp.proposalNumber}`}>
                {lp.proposalNumber} — {lp.status} (Legacy)
                {Number(lp.price) > 0 && ` — ${Number(lp.price).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
              </MenuItem>
            ))}
          </Select>
          {errors.proposal && <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>{errors.proposal}</Typography>}
        </FormControl>
      )}

      {proposals.length === 0 && legacyProposals.length === 0 && !propertyLoading && !notAirmaxxInstallation && (
        <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
          No completed/pending proposals found for this address
        </Typography>
      )}

      <FormControlLabel
        control={<Checkbox size="small" checked={notAirmaxxInstallation} onChange={(e) => handleNotAirmaxxToggle(e.target.checked)} />}
        label={<Typography variant="body2">Not Airmaxx Installation (no PO)</Typography>}
        sx={{ mb: 1.5 }}
      />

      <TextField label="Customer Phone" value={form.customerPhone} onChange={(e) => handleFormChange('customerPhone', e.target.value)} fullWidth size="small" />
    </Paper>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>New Warranty Ticket</Typography>
        {isAdminLike && (
          <Tooltip title={expertMode ? 'Switch to step-by-step wizard' : 'Show the full form at once'}>
            <FormControlLabel
              control={<Switch size="small" checked={expertMode} onChange={(e) => setExpertMode(e.target.checked)} />}
              label={<Typography variant="caption">Expert mode</Typography>}
              sx={{ m: 0 }}
            />
          </Tooltip>
        )}
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!expertMode && !propertyLoading && (
          <Box sx={{ mb: 2 }}>
            <Stepper activeStep={currentStep} alternativeLabel={!isMobile}>
              {STEP_LABELS.map((label, i) => (
                <Step key={label} completed={i < currentStep}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}
        {propertyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <WarrantyTicketFormSections ctl={ctl} dense isMobile={isMobile} addressSlot={addressSlot} />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={onClose} disabled={submitting}>Cancel</Button>
        {!expertMode && currentStep > 0 && (
          <Button variant="outlined" onClick={goPrevStep} disabled={submitting}>Back</Button>
        )}
        {!expertMode && currentStep < STEP_LABELS.length - 1 ? (
          <Button
            variant="contained"
            onClick={goNextStep}
            disabled={submitting || propertyLoading || (currentStep === 0 && !orderType)}
            sx={{ minWidth: 120 }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained" color={showReview ? 'success' : 'primary'}
            onClick={handleSubmit} disabled={submitting || propertyLoading}
            startIcon={showReview ? (submitting ? null : <SendIcon />) : null}
            sx={{ minWidth: 180 }}
          >
            {submitting ? <CircularProgress size={24} /> : showReview ? 'Create Ticket & Send Order' : 'Create Ticket'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
